import React from "react";
import { Link } from "react-router-dom";
import { TRACKS, trackQuestions, isAnswered } from "./questions";
import { PROPOSITION } from "./techQuestions";
import { AnswerInput } from "./Inputs";
import { VoiceNotes } from "./VoiceNotes";
import { Tag, Toggle } from "./Fields";

const join = (a, t) => ((a || "").trim() ? `${a.trim()}\n\n${t}` : t);
// Inputs whose text can absorb a transcript directly; others keep the note alongside the answer.
const TEXT_INPUTS = {
  long: join,
  typical_day: (v, t) => ({ ...(v || {}), narrative: join(v?.narrative, t) }),
  final: (v, t) => ({ ...(v || {}), problem: join(v?.problem, t) }),
};


export function Intro({ cfg, onRole, onBegin }) {
  return <TechIntro cfg={cfg} onBegin={onBegin} />;
}

function TechIntro({ cfg, onBegin }) {
  const tech = (cfg.track || "ceo") === "tech";
  return (
    <div className="fo-intro" data-testid="intro-screen">
      <div className="fo-head">
        <p className="fo-eyebrow">Foundation · Uburu Health{tech ? " · Technical" : ""}</p>
        <h1 className="fo-h1">Foundation</h1>
        <p className="fo-lead" style={{ fontSize: 22, color: "var(--ink)" }}>The AI Operating Layer for the Organisation</p>
        <p className="fo-lead">One subscription. Everything included. Everything unlimited. Foundation gives the organisation a complete AI operating layer for US$3,000 per month: AI intelligence, productivity applications, coding, agents, workflows, organisational knowledge, research, data analysis and executive support in one environment.</p>
      </div>
      <div className="fo-grid2" style={{ alignItems: "start" }}>
        <div className="fo-panel" data-testid="intro-unlimited">
          <span className="fo-eyebrow" style={{ color: "var(--panelMut)" }}>Everything is unlimited every month</span>
          <h2 className="fo-h2" style={{ marginTop: 10 }}>You are not stopped by tokens.</h2>
          <ul className="fo-ul" style={{ color: "var(--panelMut)", marginTop: 14 }}>{PROPOSITION.unlimited.map((u) => <li key={u}>{u}</li>)}</ul>
          <p className="muted" style={{ marginTop: 14 }}>One price. Unlimited work. Unlimited agents. Unlimited coding. Unlimited applications. No token bill.</p>
        </div>
        <div className="fo-screen-frame"><img src="/foundation/build.jpg" alt="Foundation Build: Atom Office, Work Agents, Code, App Builder" /><span className="fo-screen-cap">Build · the Foundation apps your team gets</span></div>
      </div>
      <div className="fo-card" data-testid="intro-included">
        <div className="fo-card-h"><div><h2 className="fo-h2" style={{ fontSize: 22 }}>What comes with Foundation</h2><p>Not one AI assistant. A complete environment through which the organisation works with AI.</p></div><Tag tone="ok">US$3,000 per month</Tag></div>
        <div className="fo-grid2">
          {PROPOSITION.included.map(([n, d], i) => <div key={n} className="fo-card slim" style={{ background: "rgba(255,255,255,.5)" }}><span className="fo-eyebrow">{String(i + 1).padStart(2, "0")} · {n}</span><p>{d}</p></div>)}
        </div>
        <p className="fo-hint" style={{ marginTop: 16 }}>You do not need separate subscriptions for {PROPOSITION.replaces.join(", ")}. Foundation brings the work together.</p>
      </div>
      {tech ? (
      <div className="fo-card fo-focus" data-testid="intro-technical">
        <span className="fo-eyebrow">Technical Discovery</span>
        <h2 className="fo-h2" style={{ marginTop: 8 }}>Configuring Foundation around your technology team</h2>
        <p className="fo-lead" style={{ marginTop: 12 }}>The purpose is not to audit the technology team or replace the systems they already operate. It is to understand how the team works so that Foundation can become an additional layer of capable support around them.</p>
        <p className="fo-flow">What your team owns → how your team works → what systems they use → where work gets stuck → what can be delegated → what Foundation should monitor → what agents should do → what requires approval.</p>
        <p className="fo-hint" style={{ marginTop: 12 }}>We are not asking for proprietary credentials, secrets, model weights or information that you are not authorised to provide. You can speak any answer instead of typing it.</p>
      </div>
      ) : (
      <div className="fo-card fo-focus" data-testid="intro-discovery">
        <span className="fo-eyebrow">Next</span>
        <h2 className="fo-h2" style={{ marginTop: 8 }}>Tell Foundation what it should do for Uburu.</h2>
        <p className="fo-lead" style={{ marginTop: 12 }}>Everything above is what Uburu gets. The next step is a short discovery session so we can configure the first version around the priorities of the business.</p>
      </div>
      )}
      <div><button type="button" className="fo-btn lg" onClick={onBegin} data-testid="begin-discovery">{tech ? "Continue to Technical Discovery →" : "Continue to Foundation Discovery →"}</button></div>
    </div>
  );
}

const WELCOME = {
  ceo: {
    eyebrow: "Foundation · Uburu Health",
    sub: "Building Uburu's first version of Foundation",
    purpose: "The purpose of this session is to understand what Foundation should do for Uburu and configure the first version around the priorities of the business.",
    bullets: ["what appears on Home,", "what Foundation watches,", "what it understands,", "what work it helps with,", "what agents can do,", "and what information can eventually be connected."],
    screen: "home", cap: "Foundation Home · the screen your answers will configure",
    areas: [["OUTCOMES", "What should Foundation help Uburu achieve?"], ["PROCESSES", "What should Foundation understand about how Uburu operates?"], ["TASKS", "What should Foundation do for Uburu?"], ["TOOLS", "Where should Foundation get its understanding?"]],
    note: "You can answer these at a high level. You do not need to provide confidential information, credentials or sensitive client information during this conversation. You can speak any answer instead of typing it.",
  },
  tech: {
    eyebrow: "Foundation · Uburu Health · Technical",
    sub: "Building Foundation around Uburu's technology team",
    purpose: "The purpose of this session is to understand how the technology team works so that Foundation can be configured as an additional layer of capable support around it, alongside the systems you already operate.",
    bullets: ["what Foundation reads from and never touches,", "what it monitors continuously,", "what it understands about your systems and codebase,", "what work it takes on,", "what agents may execute and what waits for approval,", "and what the first ten Foundation agents should be."],
    screen: "work-agent", cap: "Work Agents · the screen your answers will configure",
    areas: [["FUNCTION", "What does the technology team own, and who does what?"], ["SYSTEMS", "What should Foundation connect to, read and never access?"], ["WORK", "Where does the team's time go, and what should Atom Code do?"], ["AGENTS", "What should your agents monitor, alert, execute and ask first?"], ["OPERATIONS", "How are incidents, access and reporting handled today?"], ["FOUNDATION", "What would you delegate, and what should we configure first?"]],
    note: "You can answer these at a high level. We are not asking for proprietary credentials, secrets, model weights or information that you are not authorised to provide. You can speak any answer instead of typing it.",
  },
};

export function DiscoveryWelcome({ track = "ceo", onBegin, onBack }) {
  const w = WELCOME[track];
  return (
    <div className="fo-intro" data-testid="welcome-screen">
      <div className="fo-head">
        <p className="fo-eyebrow">{w.eyebrow}</p>
        <h1 className="fo-h1">{track === "tech" ? "Technical Discovery" : "Foundation Discovery"}</h1>
        <p className="fo-lead" style={{ fontSize: 22, color: "var(--ink)" }}>{w.sub}</p>
      </div>
      <div className="fo-grid2 fo-stagger" style={{ alignItems: "start" }}>
        <div className="fo-stack" style={{ gap: 16 }}>
          <p className="fo-lead"><b style={{ fontWeight: 500 }}>This is not a questionnaire.</b></p>
          <p className="fo-lead">{w.purpose}</p>
          <p className="fo-lead">Everything discussed will map directly to something Foundation can provide:</p>
          <ul className="fo-ul">{w.bullets.map((b) => <li key={b}>{b}</li>)}</ul>
        </div>
        <div className="fo-screen-frame" data-testid="welcome-screen-image">
          <img src={`/foundation/${w.screen}.jpg`} alt={w.cap} />
          <span className="fo-screen-cap">{w.cap}</span>
        </div>
      </div>
      <div className={`fo-areas fo-stagger ${w.areas.length > 4 ? "six" : ""}`} data-testid="intro-areas">
        {w.areas.map(([a, q]) => <div className="fo-card fo-area" key={a}><span className="fo-eyebrow">{a}</span><span className="q">{q}</span></div>)}
      </div>
      <p className="fo-hint" style={{ fontSize: 14 }}>{w.note}</p>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <button type="button" className="fo-btn2" onClick={onBack} data-testid="welcome-back">Back</button>
        <button type="button" className="fo-btn lg" onClick={onBegin} data-testid="begin-discovery">Begin Discovery →</button>
      </div>
    </div>
  );
}

export function SectionIntro({ section, count, onContinue, onBack }) {
  return (
    <div className="fo-intro" data-testid={`section-intro-${section.key}`}>
      <div className="fo-head">
        <p className="fo-eyebrow">Section {section.n} · {section.area}</p>
        <h1 className="fo-h1">{section.heading}</h1>
      </div>
      <div className="fo-card fo-focus">
        <span className="fo-eyebrow">Foundation's focus for Uburu</span>
        <h2 className="fo-h2" style={{ marginTop: 8 }}>{section.focus}</h2>
        <p className="fo-lead" style={{ marginTop: 12 }}>{section.intro}</p>
      </div>
      <div className="fo-foot">
        <button type="button" className="fo-btn2" onClick={onBack} data-testid="section-back">Back</button>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <span className="st">{count} question{count === 1 ? "" : "s"} in this section</span>
          <button type="button" className="fo-btn" onClick={onContinue} data-testid="section-continue">Continue</button>
        </div>
      </div>
    </div>
  );
}

export function QuestionScreen({ q, index, total, answer, onAnswer, answers, onBack, onNext, locked, configId, track = "ceo" }) {
  const section = TRACKS[track].sections.find((s) => s.key === q.section);
  const a = answer || {};
  const set = (patch) => !locked && onAnswer({ ...a, ...patch });
  return (
    <div className="fo-q" data-testid={`question-${q.id}`}>
      <aside className="fo-q-left">
        <div className="fo-screen-frame" data-testid="question-screen-image">
          <img src={`/foundation/${q.screen}.jpg`} alt={q.screenName} />
          <span className="fo-screen-cap">{q.screenName}</span>
        </div>
        <div className="fo-configures" data-testid="question-configures">
          <span className="fo-eyebrow">Your answer configures</span>
          <div className="fo-config-chain">
            <Tag tone="mut">{q.configures}</Tag>
            <span aria-hidden>→</span>
            <Tag tone="ok">{q.capability}</Tag>
          </div>
        </div>
      </aside>
      <div className="fo-q-right">
        <div className="fo-head">
          <p className="fo-eyebrow">{section.title} · Question {index} of {total}</p>
          <h1 className="fo-h1 q" data-testid="question-title">{q.question}</h1>
        </div>
        <div className="fo-grid2 fo-why">
          <div className="fo-card slim" data-testid="question-why"><span className="fo-eyebrow">Why we ask</span><p>{q.why}</p></div>
          <div className="fo-card slim" data-testid="question-uses"><span className="fo-eyebrow">What Foundation uses this for</span><p>{q.uses}</p></div>
        </div>
        <div className="fo-card fo-answer" data-testid="question-answer">
          <div className="fo-card-h">
            <div><span className="fo-eyebrow">Your answer</span>{q.hint && <p>{q.hint}</p>}</div>
            {q.routable && <Toggle on={!!a.routed_to} onChange={(v) => set({ routed_to: v ? "technical owner" : null })} label="Route to our technical owner" testId="route-toggle" />}
          </div>
          {a.routed_to ? (
            <p className="fo-hint" data-testid="routed-note">Marked for your technical or data owner. They can answer this in their own session.</p>
          ) : (
            <>
              <VoiceNotes
                configId={configId}
                q={q}
                answer={a}
                locked={locked}
                onAnswer={(next) => !locked && onAnswer(next)}
                onTranscript={TEXT_INPUTS[q.input] ? (t) => set({ value: TEXT_INPUTS[q.input](a.value, t), skipped: false }) : null}
              />
              <AnswerInput q={q} value={a.value} onChange={(value) => set({ value, skipped: false })} answers={answers} />
            </>
          )}
        </div>
        <div className="fo-foot">
          <button type="button" className="fo-btn2" onClick={onBack} data-testid="q-back">Back</button>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            {!isAnswered(a) && <button type="button" className="fo-link" onClick={() => { set({ skipped: true }); onNext(); }} data-testid="q-skip">Skip for now</button>}
            <button type="button" className="fo-btn" onClick={onNext} data-testid="q-next">{q.isFinal ? "See what this builds" : "Continue"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function FinalSummary({ cfg, onGo, onSubmit, onReopen, busy }) {
  const t = TRACKS[cfg.track || "ceo"];
  const MAPPING = t.mapping;
  const qs = trackQuestions(t.key, cfg.respondent_role);
  const answered = qs.filter((q) => isAnswered(cfg.answers?.[q.id]) && !cfg.answers?.[q.id]?.skipped);
  const skipped = qs.filter((q) => cfg.answers?.[q.id]?.skipped || !isAnswered(cfg.answers?.[q.id]));
  const submitted = cfg.status === "submitted";
  const final = cfg.answers?.final?.value || {};
  return (
    <div className="fo-intro" data-testid="final-summary">
      <div className="fo-head">
        <p className="fo-eyebrow">Foundation · Uburu Health</p>
        <h1 className="fo-h1">What your answers will build</h1>
        <p className="fo-lead">{answered.length} of {qs.length} questions answered{skipped.length ? `, ${skipped.length} left for later` : ""}. Every one has a destination inside Foundation.</p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Tag tone={submitted ? "ok" : "info"} testId="summary-status">{submitted ? `Submitted ${String(cfg.submitted_at || "").slice(0, 10)}` : "Draft"}</Tag>
          {skipped.map((q) => <button type="button" key={q.id} className="fo-pill sm" onClick={() => onGo(q.id)} data-testid={`gap-${q.id}`}>{q.label}</button>)}
        </div>
      </div>
      {t.key === "ceo" && final.problem && (
        <div className="fo-panel" data-testid="summary-first-problem">
          <span className="fo-eyebrow" style={{ color: "var(--panelMut)" }}>The first problem Foundation solves</span>
          <h2 className="fo-h2" style={{ marginTop: 10 }}>{final.problem}</h2>
          {final.success_metric && <p className="muted" style={{ marginTop: 10 }}>Proven by: {final.success_metric}</p>}
        </div>
      )}
      <div className="fo-card" style={{ padding: 0 }} data-testid="summary-mapping">
        <table className="tbl map">
          <thead><tr><th>What Uburu tells Foundation</th><th>What Foundation builds</th></tr></thead>
          <tbody>{MAPPING.map(([a, b]) => <tr key={a}><td>{a}</td><td><Tag tone="ok">{b}</Tag></td></tr>)}</tbody>
        </table>
      </div>
      <div className="fo-card fo-focus" data-testid="summary-statement">
        {t.key === "tech" ? (
          <>
            <p className="fo-lead" style={{ fontSize: 20, color: "var(--ink)" }}>Foundation understands the team, understands the work, understands the systems, monitors what matters, prepares what the team needs, executes the work it is authorised to execute and brings people into the loop when human judgement is required.</p>
            <p className="fo-flow">The subscription is US$3,000 per month. Everything is included. Everything is unlimited every month. The organisation is not stopped by tokens.</p>
          </>
        ) : (
          <>
            <p className="fo-lead" style={{ fontSize: 20 }}>The CEO is not being asked to give Crowther information.</p>
            <p className="fo-lead" style={{ fontSize: 20, color: "var(--ink)" }}>The CEO is being asked to tell Foundation what they want it to do for Uburu.</p>
            <p className="fo-flow">What matters → How the business works → What should improve → What Foundation should do → What Foundation needs to understand → What we should prove first.</p>
          </>
        )}
      </div>
      <div className="fo-foot">
        <button type="button" className="fo-btn2" onClick={() => onGo("final")} data-testid="summary-back">Back</button>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <span className="st">{submitted ? "Crowther has this. Reopen to make changes." : "You can submit now and return to anything left for later."}</span>
          {submitted ? <button type="button" className="fo-btn2" onClick={onReopen} disabled={busy} data-testid="summary-reopen">Reopen</button>
            : <button type="button" className="fo-btn" onClick={onSubmit} disabled={busy} data-testid="summary-submit">Submit to Crowther</button>}
        </div>
      </div>
      <p className="fo-hint"><Link to="/gateway-2" style={{ color: "inherit" }}>Back to Crowther</Link></p>
    </div>
  );
}
